// FILE: loading-state.tsx
// Purpose: The single centred-spinner surface every screen shows while it has nothing to render yet.
// Layer: UI primitives
// Depends on: Spinner
//
// A blank pane reads as a frozen app, and an empty transcript reads as a thread
// with no messages. Anything that waits on the server before it can paint —
// auth, the first shell snapshot, a thread's transcript — renders this instead,
// so waiting always looks the same and never looks like an answer.

import { cn } from "~/lib/utils";
import { Spinner } from "./spinner";

interface LoadingStateProps {
  /** Announced to assistive tech; also drawn unless `labelHidden` is set. */
  label: string;
  /** Keeps the label for screen readers only, for tight or decorative spots. */
  labelHidden?: boolean;
  className?: string;
}

export function LoadingState({ label, labelHidden = false, className }: LoadingStateProps) {
  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-1 flex-col items-center justify-center gap-3",
        className,
      )}
    >
      <Spinner aria-label={label} className="size-6 text-muted-foreground/70" />
      <p
        className={cn(
          "text-sm text-muted-foreground",
          labelHidden ? "sr-only" : "animate-pulse text-balance",
        )}
      >
        {label}
      </p>
    </div>
  );
}

/** The same surface sized to the viewport, for gates that render before the app shell exists. */
export function FullScreenLoadingState({
  label,
  labelHidden = false,
  className,
}: LoadingStateProps) {
  return (
    <LoadingState
      label={label}
      labelHidden={labelHidden}
      className={cn("h-dvh bg-background text-foreground", className)}
    />
  );
}
